/**
 * Per-stop activity CO₂ estimates for Trip Planner.
 * Scope: venue / ops only for one stop — trip legs already count travel.
 */

export type PlaceActivityKind =
  | "hotel"
  | "food"
  | "camping"
  | "museum"
  | "cinema"
  | "amusement"
  | "spa"
  | "nightlife"
  | "shopping"
  | "attraction";

/** ADEME-style ~1 guest-night (all-in hotel night ~9.9 kg; we keep 10.5). */
export const HOTEL_KG_CO2_PER_NIGHT = 10.5;
/** Conservative restaurant outing (~1 meal + venue); ADEME average meal ~2 kg. */
export const FOOD_KG_CO2_PER_MEAL = 5.0;
/** ADEME / Impact CO₂ camping night (site energy + infra). */
export const CAMPING_KG_CO2_PER_NIGHT = 1.4;
/** Farreny et al. museum energy ~2.34 kg CO₂e / visit. */
export const MUSEUM_KG_CO2_PER_VISIT = 2.3;
/** Cinéo FR ~4 kg/entry with ~69% travel stripped → ~1.2 kg venue ops. */
export const CINEMA_KG_CO2_PER_VISIT = 1.2;
/** Nordic park/zoo day-visit ops (~1 kg; excl. food & travel). */
export const AMUSEMENT_KG_CO2_PER_VISIT = 1.0;
/** SPA-DEC peer-reviewed spa visit ~3.5–5 kg; mid default 4.0. */
export const SPA_KG_CO2_PER_VISIT = 4.0;
/** Sparse data; between cinema ops and spa energy intensity. */
export const NIGHTLIFE_KG_CO2_PER_VISIT = 2.5;
/** Mall/store building ops for a browse stop (excl. purchased goods). */
export const SHOPPING_KG_CO2_PER_VISIT = 1.5;
/** Generic tourist attraction ops placeholder. */
export const ATTRACTION_KG_CO2_PER_VISIT = 1.5;

const ACTIVITY_KG_CO2: Record<PlaceActivityKind, number> = {
  hotel: HOTEL_KG_CO2_PER_NIGHT,
  food: FOOD_KG_CO2_PER_MEAL,
  camping: CAMPING_KG_CO2_PER_NIGHT,
  museum: MUSEUM_KG_CO2_PER_VISIT,
  cinema: CINEMA_KG_CO2_PER_VISIT,
  amusement: AMUSEMENT_KG_CO2_PER_VISIT,
  spa: SPA_KG_CO2_PER_VISIT,
  nightlife: NIGHTLIFE_KG_CO2_PER_VISIT,
  shopping: SHOPPING_KG_CO2_PER_VISIT,
  attraction: ATTRACTION_KG_CO2_PER_VISIT,
};

export const PLACE_ACTIVITY_TOOLTIP: Record<PlaceActivityKind, string> = {
  hotel:
    "Estimated stay emissions: ADEME-style average ~10.5 kg CO₂ per guest-night (hotel energy, services, and infra). Travel is counted separately on trip legs.",
  food:
    "Estimated meal emissions: ~5.0 kg CO₂ for one restaurant-style outing (ADEME meals are ~2 kg on average; this includes a fuller meal + venue). Travel is counted separately.",
  camping:
    "Estimated camping night: ADEME / Impact CO₂ ~1.4 kg CO₂ for site energy and shared infra (showers, lighting). Travel is counted separately.",
  museum:
    "Estimated museum visit: ~2.3 kg CO₂ from venue energy use (research average ~2.34 kg CO₂e/visit). Visitor travel is counted separately on trip legs.",
  cinema:
    "Estimated cinema visit: ~1.2 kg CO₂ for venue operations. Published ~4 kg/entry includes travel (~70%); we keep ops only because legs already count travel.",
  amusement:
    "Estimated amusement/zoo day: ~1.0 kg CO₂ for park operations (Nordic park studies; excl. food purchases and travel). Travel is on trip legs.",
  spa:
    "Estimated spa/wellness visit: ~4.0 kg CO₂ (peer-reviewed SPA-DEC range ~3.5–5 kg per visit). Travel is counted separately.",
  nightlife:
    "Estimated nightlife stop: ~2.5 kg CO₂ for venue energy (lighting/HVAC). Approximate ops-only figure; travel is on trip legs.",
  shopping:
    "Estimated shopping stop: ~1.5 kg CO₂ for building operations during a browse visit. Does not include purchased goods. Travel is on trip legs.",
  attraction:
    "Estimated attraction visit: ~1.5 kg CO₂ for venue operations (generic tourist site). Travel is counted separately on trip legs.",
};

/** First matching Google Places type wins (more specific before generic). */
const GOOGLE_TYPE_TO_ACTIVITY: Record<string, PlaceActivityKind> = {
  lodging: "hotel",
  restaurant: "food",
  cafe: "food",
  bar: "food",
  bakery: "food",
  meal_takeaway: "food",
  meal_delivery: "food",
  campground: "camping",
  rv_park: "camping",
  museum: "museum",
  art_gallery: "museum",
  movie_theater: "cinema",
  amusement_park: "amusement",
  zoo: "amusement",
  aquarium: "amusement",
  spa: "spa",
  night_club: "nightlife",
  casino: "nightlife",
  shopping_mall: "shopping",
  department_store: "shopping",
  clothing_store: "shopping",
  store: "shopping",
  supermarket: "shopping",
  tourist_attraction: "attraction",
};

const PLACE_TYPE_NOISE = new Set([
  "geocode",
  "political",
  "establishment",
  "point_of_interest",
  "premise",
  "subpremise",
  "plus_code",
  "route",
  "street_address",
]);

type PlaceTyped = {
  placeTypes?: string[];
};

export const resolvePlaceActivityKind = (
  placeTypes?: string[]
): PlaceActivityKind | undefined => {
  if (!placeTypes?.length) {
    return undefined;
  }

  for (const type of placeTypes) {
    if (PLACE_TYPE_NOISE.has(type)) {
      continue;
    }
    const kind = GOOGLE_TYPE_TO_ACTIVITY[type];
    if (kind) {
      return kind;
    }
  }

  return undefined;
};

/** Activity CO₂ (kg) for a stop, if the place type has an estimate. */
export const getPlaceActivityEmissionKg = (
  placeTypes?: string[]
): number | undefined => {
  const kind = resolvePlaceActivityKind(placeTypes);
  return kind ? ACTIVITY_KG_CO2[kind] : undefined;
};

/** Chip tooltip explaining the activity estimate basis. */
export const getPlaceActivityTooltip = (
  placeTypes?: string[]
): string | undefined => {
  const kind = resolvePlaceActivityKind(placeTypes);
  return kind ? PLACE_ACTIVITY_TOOLTIP[kind] : undefined;
};

/** Sum of activity estimates across stops. */
export const sumPlaceActivityEmissionKg = (
  locations: PlaceTyped[]
): number =>
  locations.reduce((total, location) => {
    const kg = getPlaceActivityEmissionKg(location.placeTypes);
    return total + (kg ?? 0);
  }, 0);
